<template>
    <VDialog
        v-bind="$attrs"
        v-model="model"
        max-height="80vh"
        max-width="90vw"
        class="k-dialog"
        :persistent="!closeOnOutSideClick"
        :width="width"
        :height="height"
        :style="{ boxShadow: '0px 4px 24px 0px #0000001f !important' }"
        :opacity="0.1"
        elevation="0"
    >
        <VSheet
            :elevation="2"
            height="100%"
            width="100%"
            class="d-flex flex-column justify-space-between align-center"
            :style="{
                borderRadius: '4px',
                backgroundColor: 'rgba(184, 184, 184, 1)',
            }"
            :class="{
                'pb-3': noFooter,
            }"
        >
            <!-- header -->
            <div
                class="k-auth-dialog_header d-flex align-center justify-space-between w-100 px-4"
                :style="{ backgroundColor: 'rgba(184, 184, 184, 1)' }"
            >
                <div class="d-flex justify-center pl-2">
                    <slot name="dialog_header" :logo="logo">
                        <VImg
                            style="margin-top: 4px"
                            :src="logo"
                            height="auto"
                            width="4.5rem"
                            alt="Dialog"
                            eager
                        />
                    </slot>
                </div>
                <Icon
                    google
                    color="rgba(0, 0, 0, 1)"
                    size="20"
                    class="cursor-pointer"
                    @click="handleClose"
                >
                    close
                </Icon>
            </div>
            <!-- body -->
            <div
                v-if="!noHeader"
                class="k-auth-dialog_body w-100 flex-grow-1 overflow-scroll prospus-scrollbar py-6"
                :class="[thin ? 'px-3' : 'px-6']"
                :style="{
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    paddingRight: !thin ? '2.5rem !important' : '',
                }"
            >
                <div class="d-flex align-start justify-start ga-6 h-100">
                    <div v-if="faceLogo" class="mt-1">
                        <KAvatar
                            :image-src="faceLogo"
                            name=""
                            bg-color=""
                            rounded="0"
                            :size="orgOwnerInviteIcon ? 'mx-large' : 'x-large'"
                        />
                    </div>
                    <div
                        class="d-flex align-start flex-column justify-start w-100 h-100"
                    >
                        <slot name="dialog_title">
                            <template v-if="title">
                                <h2 class="k-dialog_title">
                                    {{ title }}
                                </h2>
                                <v-divider class="w-100 my-2 mt-2 mb-3" />
                            </template>
                        </slot>

                        <slot name="dialog_body">
                            <slot name="body_top" />
                            <div class="d-flex align-start justify-start ga-3">
                                <slot name="body_prepend" />
                                <div>
                                    <div class="w-100">
                                        <slot name="body_middle_top" />
                                    </div>
                                    <p
                                        v-if="subtitle"
                                        v-html="subtitle"
                                        class="k-dialog_subtitle"
                                    />
                                    <div class="w-100">
                                        <slot name="body_middle_bottom" />
                                    </div>
                                </div>
                                <slot name="body_append" />
                            </div>
                            <slot name="body_bottom" />
                        </slot>
                    </div>
                </div>
            </div>
            <!-- footer -->
            <template v-if="!noFooter">
                <v-divider class="w-100" color="rgba(227, 227, 227, 1)" />
                <!-- footer -->
                <div
                    class="d-flex justify-end align-center w-100 pl-7 k-auth-dialog_footer"
                    :style="{
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        paddingRight: '1.3rem !important',
                    }"
                >
                    <slot name="dialog_actions_prepend" />
                    <v-spacer />
                    <slot name="dialog_actions">
                        <VBtn
                            v-if="cancelLabel"
                            variant="plain"
                            class="px-3"
                            :style="{
                                fontWeight: 400,
                                height: '29px',
                                color: 'rgba(140, 140, 140, 1)',
                            }"
                            :disabled="loading"
                            :loading="cancelLoading"
                            @click="handleCancel"
                        >
                            {{ cancelLabel }}
                        </VBtn>
                        <slot name="extra_actions" />
                        <VBtn
                            v-if="confirmLabel"
                            variant="outlined"
                            class="auth-dialog-outline-btn"
                            rounded="xl"
                            :style="{
                                fontWeight: 400,
                                height: '29px',
                            }"
                            :loading="loading"
                            :disabled="loading || disableConfirm"
                            @click="handleSubmit"
                        >
                            {{ confirmLabel }}
                        </VBtn>
                    </slot>
                </div>
            </template>
        </VSheet>
    </VDialog>
</template>

<script setup>
import { computed } from "vue";

import KAvatar from "./KAvatar.vue";
import Icon from "../../../../../Admin/js/Components/Common/Icon.vue";

const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true,
    },
    height: {
        type: String,
        default: "auto",
    },
    width: {
        type: String,
        default: "35rem",
    },
    logo: {
        type: String,
        default: "/images/kaamfu-text-logo.png",
    },
    faceLogo: {
        type: String,
        default: "",
    },
    title: {
        type: String,
        default: "",
    },
    subtitle: {
        type: String,
        default: "",
    },
    noHeader: {
        type: Boolean,
        default: false,
    },
    noFooter: {
        type: Boolean,
        default: false,
    },
    confirmLabel: {
        type: String,
        default: "Yes",
    },
    cancelLabel: {
        type: String,
        default: "Cancel",
    },
    loading: {
        type: Boolean,
        default: false,
    },
    closeOnOutSideClick: {
        type: Boolean,
        default: false,
    },
    disableConfirm: {
        type: Boolean,
        default: false,
    },
    thin: {
        type: Boolean,
        default: false,
    },
    preventModelCloseOnCloseBtn: {
        type: Boolean,
        default: false,
    },
    preventCancelOnCloseClick: {
        type: Boolean,
        default: false,
    },
    orgOwnerInviteIcon: {
        type: Boolean,
        default: false,
    },
    cancelLoading: {
        type: Boolean,
        default: false,
    },
    emitConfirmOnIconClose: {
        type: Boolean,
        default: false,
    },
    preventOnCancelOnCloseClick: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits([
    "update:modelValue",
    "onCancel",
    "onConfirm",
    "onClose",
]);

const model = computed({
    get() {
        return props.modelValue;
    },
    set(value) {
        emit("update:modelValue", value);
    },
});

function handleCancel() {
    if (!props.preventModelCloseOnCloseBtn) model.value = false;
    if (!props.preventCancelOnCloseClick) emit("onCancel");
    if (props.emitConfirmOnIconClose) emit("onConfirm");
}

function handleSubmit() {
    emit("onConfirm");
}

function handleClose() {
    emit("onClose");
    if (props.preventOnCancelOnCloseClick) {
        model.value = false;
        return;
    } else {
        handleCancel();
    }
}
</script>

<style lang="scss">
@import "../../../../sass/_theme-variables.sass";

.k-auth-dialog_header {
    min-height: 41px;
}

.k-auth-dialog_body {
    border-bottom: 1px solid $sectionBorder;
}

.k-auth-dialog_footer {
    min-height: 41px;
}

.auth-dialog-outline-btn {
    border: 1px solid rgba(248, 248, 248, 1);
    background-color: rgba(216, 216, 216, 1);
    border-radius: 24px;
    height: 26px;
    gap: 1px;
    &:hover {
        background-color: rgba(242, 242, 242, 1);
        color: rgba(0, 0, 0, 1);
    }
}
</style>
